// Type-only import so socket.io-client (~88KB) is NOT in the bundle of every
// route. The runtime `io` is dynamic-imported inside createSocketConnection,
// which only the wardrobe-upload flow reaches — the shop never opens a socket.
import type { Socket } from 'socket.io-client';

/**
 * WebSocket event payload types
 */
export interface WardrobeItemPayload {
  id: string;
  name: string;
  imageUrl: string;
  category: string;
  subCategory?: string;
  colors?: string[];
  wardrobeId?: string;
}

export interface BatchProgressPayload {
  current: number;
  total: number;
  item?: WardrobeItemPayload;
}

export interface ProcessingStepPayload {
  step: 'uploading' | 'analyzing' | 'extracting' | 'generating-catalog' | 'removing-background' | 'saving' | 'complete';
  message: string;
  progress: number;
  itemIndex?: number;
  totalItems?: number;
}

export interface WardrobeItemDeletedPayload {
  id: string;
}

export interface UploadItemReadyPayload {
  jobId: string;
  itemId: string;
  name: string;
  imageUrl: string;
  catalogUrl?: string;
  category: string;
  colorFamily?: string;
  processedCount: number;
  totalCount: number;
}

export interface UploadItemFailedPayload {
  jobId: string;
  itemId: string;
  reason: string;
  creditsRefunded: number;
  processedCount: number;
  totalCount: number;
}

export interface UploadJobCompletePayload {
  jobId: string;
  status: 'completed' | 'partial' | 'failed';
  totalItems: number;
  successfulItems: number;
  failedItems: number;
  creditsRefunded: number;
}

export interface SetProcessingStepPayload {
  analysisId: string;
  step: 'generating-piece-image' | 'creating-catalog' | 'extracting-piece' | 'saving-piece' | 'creating-coord' | 'creating-outfit' | 'complete';
  message: string;
  progress: number;
  currentPiece?: number;
  totalPieces?: number;
}

/** A notification row pushed live so the notification centre updates without a refetch. */
export interface NotificationEventPayload {
  id: string;
  type: string;
  title: string;
  body: string;
  data: Record<string, unknown> | null;
  readAt: string | null;
  createdAt: string;
}

/**
 * WebSocket event types
 */
export type SocketEvents = {
  'wardrobe:item-created': (item: WardrobeItemPayload) => void;
  'wardrobe:item-deleted': (payload: WardrobeItemDeletedPayload) => void;
  'wardrobe:batch-progress': (payload: BatchProgressPayload) => void;
  'wardrobe:processing-step': (payload: ProcessingStepPayload) => void;
  'upload:item-ready': (payload: UploadItemReadyPayload) => void;
  'upload:item-failed': (payload: UploadItemFailedPayload) => void;
  'upload:job-complete': (payload: UploadJobCompletePayload) => void;
  'set:processing-step': (payload: SetProcessingStepPayload) => void;
};

/**
 * Get the WebSocket URL from the API URL
 * For staging: https://stagingback.heyhoku.ai -> wss://stagingback.heyhoku.ai
 * For production: https://back.heyhoku.ai -> wss://back.heyhoku.ai
 */
function getWebSocketUrl(): string {
  // Use the same API URL that the proxy uses
  // This is set in .env as NEXT_PUBLIC_API_URL
  const apiUrl = process.env.NEXT_PUBLIC_API_URL || '';

  if (!apiUrl) {
    // Fallback to localhost for local development only
    if (typeof window !== 'undefined' && window.location.hostname === 'localhost') {
      return 'ws://localhost:5002';
    }
    // For staging/production, derive from current hostname
    const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
    if (hostname.includes('staging')) {
      return 'wss://stagingback.heyhoku.ai';
    }
    return 'wss://back.heyhoku.ai';
  }

  // Convert HTTP(S) to WS(S)
  return apiUrl.replace(/^http/, 'ws');
}

/**
 * Create a Socket.io client connection
 */
export async function createSocketConnection(userId: string): Promise<Socket> {
  // Load socket.io-client on demand — keeps it off every route's first-load bundle.
  const { io } = await import('socket.io-client');
  const wsUrl = getWebSocketUrl();

  console.log(`[WebSocket] Connecting to ${wsUrl} for user ${userId}`);

  const socket = io(wsUrl, {
    auth: {
      userId,
    },
    transports: ['websocket', 'polling'],
    reconnection: true,
    reconnectionAttempts: 5,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 5000,
    timeout: 20000,
  });

  socket.on('connect', () => {
    console.log('[WebSocket] Connected successfully');
  });

  socket.on('disconnect', (reason) => {
    console.log(`[WebSocket] Disconnected: ${reason}`);
  });

  socket.on('connect_error', (error) => {
    console.error('[WebSocket] Connection error:', error.message);
  });

  return socket;
}
